/* eslint-disable no-alert */
/** @jsxImportSource @emotion/react */
import { PrinterOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import appOrigin from '@sportninja/common/constants/appOrigin'
import { useScheduleSettings } from '@sportninja/common/hooks/useScheduleSettings'
import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { convertTimesToISOStrings } from '@sportninja/common/sagas/game'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import {
  getAllGenericSportProperties,
  getSports,
} from '@sportninja/common/selectors/types'
import { getSelfUser } from '@sportninja/common/selectors/users'
import { setTimeToBeDefined } from '@sportninja/common/ts-utils/date-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Dropdown, Spin } from 'antd'
import dayjs from 'dayjs'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import GeneratePluginModal from 'src/components/GeneralPluginModal'
import Icon from 'src/components/Icon'
import OfficialListTable from 'src/components/List/OfficialListTable'
import { NewModal } from 'src/components/NewModal'
import PrintSignSheetsModal from 'src/components/PrintSignInSheetsModal'
import ScheduleModal from 'src/components/ScheduleModal'
import ShareModal from 'src/components/ShareModal'
import ExportIcon from 'src/components/icons/ExportIcon'
import ImportIcon from 'src/components/icons/ImportIcon'
import ShareIcon from 'src/components/icons/ShareIcon'
import CompetitionSetupWizard from 'src/components/ts-components/CompetitionSetupWizard'
import Text from 'src/components/ts-components/Text'
import { useCompetitionSetupTutorial } from 'src/hooks/useCompetitionSetupTutorial'
import LocationModal from 'src/components/LocationModal'
import FileDownload from '../../components/FileDownload'
import FormSheet from '../../components/Form/FormSheet'
import { feedItemForm, gameForm } from '../../components/Form/form-configs'
import ImportCSV from '../../components/ImportCSV'
import GameList from '../../components/List/GameList'
import { AddOfficial } from '../../components/List/OfficialList'
import NewReports from '../../components/NewReports'
import Page from '../../components/Page'
import BaseActionButton from '../../components/Page/BaseActionButton'
import EditButton from '../../components/Page/EditButton'
import FavouriteButton from '../../components/Page/FavouriteButton'
import NewButton from '../../components/Page/NewButton'
import { Mobile } from '../../components/Responsive'
import { ScheduleReprocessingContext } from '../../components/ScheduleReprocessingContext'

import Suspensions from '../../components/Suspensions'
import AddSuspensionSheet from '../../components/Suspensions/AddSuspensionSheet'
import useGetSuspensions from '../../components/Suspensions/useGetSuspensions'
import Venues from '../../components/Venues'
import Standings from '../../components/v2/Standings'
import Statistics from '../../components/v2/Statistics'
import { FrameContext } from '../App'
import {
  getBreadcrumbs,
  refreshScheduleAfterGamesChange,
  useReadItem,
  useStoreRecent,
} from '../utils'
import CloneSchedule from './CloneSchedule'
import { EmptySchedule } from './EmptySchedule'
import ScheduleEmptyState from 'src/components/ScheduleEmptyState'
import ScheduleSubTabs from './ScheduleSubTabs'
import GameSlots from './GameSlots'
import RegistrationPageEntry from '../registration'
import CouponsPage from '../coupons'
import RegistrationSetup from './RegistrationSetup'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import ReprocessStats from './ReprocessStats'
import ReprocessingSettingsOverlay from './ReprocessingSettingsOverlay'
import ScheduleBracket from './ScheduleBracket'
import FullScreenBracketModal from './ScheduleBracket/FullScreenBracketModal'
import ScheduleTree from './ScheduleTree'
import EntityFeed from 'src/components/EntityFeed'
import UserListTable from 'src/components/List/UserListTable'
import UserModal from 'src/components/UserModal'

const generateHrefAndDownloadCsv = (string, reportName) => {
  var blob = new Blob([string], { type: 'text/csv;charset=utf-8;' })

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, reportName)
  } else {
    var a = document.createElement('a')
    document.body.appendChild(a)
    a.style = 'display: none'
    var csvUrl = URL.createObjectURL(blob)
    a.href = csvUrl
    a.download = reportName
    a.click()
    URL.revokeObjectURL(a.href)
    a.remove()
  }
}

/**
 * Generates a registration name from a schedule object.
 *
 * @param {Object} schedule - The schedule object.
 * @param {string} schedule.name - The name of the competition.
 * @param {string} schedule.starts_at - The start date of the competition.
 * @param {string} schedule.ends_at - The end date of the competition.
 * @returns {string} The registration name in the format: Registrations_${Competition Name}_${DateStartsAt}_${DateEndsAt}.
 */
const generateRegistrationName = (schedule, label = 'Registrations') => {
  const competitionName = (schedule.name || 'Unknown')
    .toLowerCase()
    .replace(/\s/g, '_')
  const dateStartsAt = schedule.starts_at
    ? new Date(schedule.starts_at).toISOString().split('T')[0].replace(/-/g, '')
    : 'Unknown'
  const dateEndsAt = schedule.ends_at
    ? new Date(schedule.ends_at).toISOString().split('T')[0].replace(/-/g, '')
    : 'Unknown'

  const filename =
    `${competitionName}_${dateStartsAt}_${dateEndsAt}_${label}`?.toLocaleLowerCase()
  return filename.endsWith('.csv') ? filename : `${filename}.csv`
}

const Schedule = ({
  match,
  isInFrame,
  genericSportProperties,
  sportTypes,
  me,
}) => {
  const frameConfig = useContext(FrameContext)
  const history = useHistory()
  const location = useLocation()

  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
    features,
  } = useReadItem()

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [initialState, setInitialState] = useState(null)
  const SCHEDULE_ID = match.params.id?.replace('?sandbox_tour=1', '')
  const BASE_ROUTE = `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}`
  const IS_PUBLIC = item.is_public
  const TOURNAMENT_READY = item.is_tournament
  const syncSalesforce = item?.organization?.sync_salesforce || false
  const canSyncGameSlots = item?.tournament_can_sync_regular_season

  // These values are used for user-triggered actions (print/export)
  // Using a ref instead of state to avoid re-render cascades when GameList
  // appends pages (which would cause items/pages useMemos to recompute and
  // reset scroll position)
  const visibleGameIdsRef = useRef([])
  const setVisibleGameIds = useCallback((valueOrUpdater) => {
    if (typeof valueOrUpdater === 'function') {
      visibleGameIdsRef.current = valueOrUpdater(visibleGameIdsRef.current)
    } else {
      visibleGameIdsRef.current = valueOrUpdater
    }
  }, [])
  const [filterForExporting, setFilterForExporting] = useState(null)

  // Extract export schedule ID from filter state (for division/conference filtering)
  const exportScheduleId = filterForExporting?._exportScheduleId || SCHEDULE_ID

  // SN-7619: When time-period filter (Past/Today/Upcoming) is active, print
  // scoresheets/sign-in sheets should include ALL games, not just the filtered subset.
  // Set this to `true` to revert to old behavior (only print visible/filtered games).
  const PRINT_ONLY_VISIBLE_GAMES = false

  // Fetches all game IDs across all pages using the export filter (which excludes
  // time-period dates). Falls back to visibleGameIdsRef when PRINT_ONLY_VISIBLE_GAMES is true.
  const fetchAllGameIdsForPrint = useCallback(async () => {
    if (PRINT_ONLY_VISIBLE_GAMES) {
      return visibleGameIdsRef.current
    }

    const scheduleId = exportScheduleId || SCHEDULE_ID
    const { _exportScheduleId, ...query } = filterForExporting || {}
    query.exclude_cancelled_games = 1

    let allIds = []
    let page = 1
    let hasMore = true

    while (hasMore) {
      const response = await req(`/schedules/${scheduleId}/games`, {
        query: { ...query, page },
      })
      const ids = response.data?.map((g) => g.id) || []
      allIds = [...allIds, ...ids]
      const pagination = response.meta?.pagination
      hasMore = pagination && pagination.current_page < pagination.total_pages
      page++
    }

    return allIds
  }, [
    PRINT_ONLY_VISIBLE_GAMES,
    SCHEDULE_ID,
    exportScheduleId,
    filterForExporting,
  ])

  const [isPrintLoading, setIsPrintLoading] = useState(false)
  const [areFiltersHidden, setAreFiltersHidden] = useState(false)
  const [isFilterToggleDisabled, setIsFilterToggleDisabled] = useState(false)
  const [ifNecessaryStatus] = useState(false)
  const [selectedGameSlotDivision, setSelectedGameSlotDivision] = useState(null)
  const [selectedGameSlotDivisionName, setSelectedGameSlotDivisionName] =
    useState(null)
  const [gamesCreated, setGamesCreated] = useState(false)
  const [hasTournamentStarted, setHasTournamentStarted] = useState(false)
  const [onFinishUploadGameslots, setOnFinishUploadGameslots] = useState(false)
  const [hasTeams, setHasTeams] = useState(false)
  const [loadingTeams, setLoadingTeams] = useState(true)

  // Competition Setup Wizard
  // Check if user was redirected from onboarding wizard
  const urlParams = new URLSearchParams(location.search)
  const isFromOnboarding = urlParams.get('fromOnboarding') === 'true'

  // Use the actual schedule ID from URL params instead of hardcoded value
  const {
    shouldShowModal: shouldShowCompetitionWizard,
    updateTutorialStatus: updateCompetitionTutorialStatus,
    closeModal: closeCompetitionWizard,
    tutorialStatus,
  } = useCompetitionSetupTutorial({
    competitionId: SCHEDULE_ID,
    isLoggedIn: true,
    isAdmin: permission?.admin || false,
    userEmail: me?.email,
    isFromOnboarding,
  })

  const [isFullScreenBracketModalOpen, setIsFullScreenBracketModalOpen] =
    useState(false)
  const [bracketScheduleId, setBracketScheduleId] = useState(null)
  const [bracketDivision, setBracketDivision] = useState(null)
  const [bracketCompetitionType, setBracketCompetitionType] = useState(null)

  const sports = useMemo(() => {
    if (!sportTypes || Object.keys(sportTypes).length === 0) {
      return []
    }
    return Object.entries(sportTypes).map(([id, sport]) => ({
      id,
      name: sport.name,
      name_full: sport.name_full,
    }))
  }, [sportTypes])

  const sport = useMemo(() => {
    return sports?.find((s) => s.id === item?.sport_id)
  }, [sports, item?.sport_id])
  const isSoccer =
    item?.sport_id === sports?.find((s) => s.name_full === 'Soccer')?.id
  const isHockey =
    item?.sport_id === sports?.find((s) => s.name === 'hockey')?.id

  const generatePluginTagModalRef = useRef(null)
  const printSignInSheetModalRef = useRef(null)
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false)
  const isProfessionalOrUltimateAccount = true
  const [isRegistrationFormOpen, setIsRegistrationFormOpen] = useState(false)
  const openCouponCreateRef = useRef(null)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const genericSport = useMemo(() => {
    if (item?.generic_sport_id) {
      return genericSportProperties.find((s) => s.id === item.generic_sport_id)
    }
    return null
  }, [item, genericSportProperties])
  const isGenericSport = genericSport !== null && genericSport?.id !== null

  const [isRegistrationSetup, setIsRegistrationSetup] = useState(false)
  const [registration, setRegistration] = useState(null)
  const [registrationTypes, setRegistrationTypes] = useState(null)
  const [availableTaxes, setAvailableTaxes] = useState(null)
  const refetchRegistrationFn = useRef(null)
  const refetchRegistrationInfoFn = useRef(null)
  const displayEnabledRegistrationTab = useMemo(() => {
    return !isInFrame && permission.admin && item?.is_registration_on
  }, [isInFrame, permission.admin, item?.is_registration_on])
  const displayDisabledRegistrationTab = useMemo(() => {
    return !isInFrame && permission.admin && !item?.is_registration_on
  }, [isInFrame, permission.admin, item?.is_registration_on])

  useStoreRecent(ENTITY_TYPES.schedule, item, error, SCHEDULE_ID, isInFrame)

  const {
    scheduleSettings,
    fetchScheduleSettings,
    error: scheduleSettingsError,
    loading: scheduleSettingsLoading,
  } = useScheduleSettings()

  const assignPointsToPlayer =
    scheduleSettings?.settings?.game?.scoring?.assign_points_to_player || false

  const shouldDisplayStatistics = useMemo(() => {
    if (isGenericSport) {
      // If assign points to player is true, we should display statistics
      if (assignPointsToPlayer) {
        return true
      } else {
        return false
      }
    }
    return true
  }, [isGenericSport, assignPointsToPlayer])

  useEffect(() => {
    if (
      SCHEDULE_ID &&
      !scheduleSettings &&
      !scheduleSettingsLoading &&
      !scheduleSettingsError
    ) {
      fetchScheduleSettings(SCHEDULE_ID)
    }
  }, [
    SCHEDULE_ID,
    fetchScheduleSettings,
    scheduleSettings,
    scheduleSettingsError,
    scheduleSettingsLoading,
  ])

  useEffect(() => {
    readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SCHEDULE_ID])

  // Clean up fromOnboarding URL parameter after wizard is handled
  useEffect(() => {
    if (isFromOnboarding && tutorialStatus) {
      // Remove fromOnboarding parameter from URL after wizard logic has been processed
      const newUrlParams = new URLSearchParams(location.search)
      newUrlParams.delete('fromOnboarding')
      const newSearch = newUrlParams.toString()
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname

      // Only update URL if it actually changes
      if (newUrl !== window.location.pathname + window.location.search) {
        history.replace(newUrl)
      }
    }
  }, [isFromOnboarding, tutorialStatus, location, history])

  // Redirect to registration tab on initial load if we're on the root schedule page,
  // registration is enabled, and there are no games scheduled
  const hasCheckedRegistrationRedirect = useRef(null)
  useEffect(() => {
    if (hasCheckedRegistrationRedirect.current === SCHEDULE_ID || loading) {
      return
    }
    if (!item || !permission || item.id !== SCHEDULE_ID) {
      return
    }
    hasCheckedRegistrationRedirect.current = SCHEDULE_ID
    if (isInFrame) {
      return
    }
    if (!permission.admin || !item.is_registration_on || item.games_count > 0) {
      return
    }
    const currentPath = window.location.pathname
    const rootPath = `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}`
    const registrationPath = `${rootPath}/registration`

    if (currentPath === rootPath) {
      history.replace(registrationPath)
    }
  }, [item, permission, SCHEDULE_ID, history, isInFrame, loading])

  // Check for registration modal query parameter and open modal
  useEffect(() => {
    const registrationUrlParams = new URLSearchParams(location.search)
    if (
      registrationUrlParams.get('openRegistrationModal') === 'true' &&
      permission?.admin
    ) {
      setIsRegistrationFormOpen(true)

      // Clean up URL by removing the query parameter
      const newParams = new URLSearchParams(location.search)
      newParams.delete('openRegistrationModal')
      const newSearch = newParams.toString()
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname

      history.replace(newUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, permission?.admin, history])

  // Redirect to root if user is on registration tab but displayDisabledRegistrationTab is false
  // This is to prevent the user from being stuck on the registration tab if the registration is disabled
  useEffect(() => {
    if (displayDisabledRegistrationTab === undefined) {
      return
    }
    if (!item || Object.keys(item).length === 0) {
      return
    }
    if (!permission || Object.keys(permission).length === 0) {
      return
    }
    if (item?.is_registration_on) {
      return
    }
    const currentPath = window.location.pathname
    const registrationPath = `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/registration`
    if (currentPath === registrationPath) {
      history.replace(`${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}`)
    }
  }, [displayDisabledRegistrationTab, SCHEDULE_ID, history, item, permission])

  const { listeners = {}, addPending } = useContext(ScheduleReprocessingContext)
  const scheduleReprocessingListener = listeners[SCHEDULE_ID]
  const [dismissed, setDismissed] = useState(false)

  const {
    loading: isSuspensionsLoading,
    suspensions,
    refresh,
  } = useGetSuspensions(SCHEDULE_ID)

  useEffect(() => {
    const checkTeams = async () => {
      try {
        const response = await req(`/schedules/${SCHEDULE_ID}/teams/simple`)
        if (response?.data?.length > 0) {
          setHasTeams(true)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingTeams(false)
      }
    }

    if (
      (!hasTeams &&
        window?.location?.pathname === `/schedule/${SCHEDULE_ID}`) ||
      window?.location?.pathname === `/schedule/${SCHEDULE_ID}/schedule`
    ) {
      checkTeams()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTeams, window?.location?.pathname])

  const { DateTime } = useTimezoneManager({
    date: null,
    timezone: null,
  })

  const handleRefreshScheduleAfterGamesChange = useCallback(() => {
    refreshScheduleAfterGamesChange(readItem, triggerRefresh, SCHEDULE_ID)
  }, [SCHEDULE_ID, readItem, triggerRefresh])

  const onAddGame = useCallback(
    async ({ ...form }) => {
      const body = convertTimesToISOStrings(form, ['started_at', 'ended_at'])
      const gameToBeDefined = form?.time_to_be_defined_status || false
      const duration = form?.duration || 0

      const userTimeStartDate = DateTime.fromISO(form?.starts_at)
      const locationStartDate = userTimeStartDate.setZone(
        form?.location_timezone,
        {
          keepLocalTime: true,
        }
      )

      const startToBeDefined = setTimeToBeDefined(
        locationStartDate.toUTC().toISO()
      )

      const startToBeDefinedWithAddedDuration = dayjs(startToBeDefined)
        .add(duration, 'minute')
        .utc()
        .toISOString()

      const userTimeEndDate = DateTime.fromISO(form?.ends_at)
      const locationEndDate = userTimeEndDate.setZone(form?.location_timezone, {
        keepLocalTime: true,
      })

      // For web: if no game_type_id was selected, set the default value as 2 (i.e. regular season)
      if (!body.game_type_id) {
        body.game_type_id = 2
      } else if (Object.prototype.hasOwnProperty.call(body, 'game_type_id')) {
        body.game_type_id = parseInt(body.game_type_id, 10)
      }

      const response = await req(`/schedules/${SCHEDULE_ID}/games`, {
        method: 'POST',
        body: JSON.stringify({
          ...body,
          starts_at: gameToBeDefined
            ? setTimeToBeDefined(locationStartDate.toUTC().toISO())
            : locationStartDate.toUTC().toISO(),
          ends_at: gameToBeDefined
            ? startToBeDefinedWithAddedDuration
            : locationEndDate.toUTC().toISO(),
          is_upcoming_livestream: form?.is_upcoming_livestream || false,
        }),
      })

      handleRefreshScheduleAfterGamesChange()
      return response
    },
    [SCHEDULE_ID, DateTime, handleRefreshScheduleAfterGamesChange]
  )

  const uploadCompetition = useCallback(
    (label, onClick) => (
      <BaseActionButton
        name='upload'
        title={label}
        onClick={onClick}
        id='import-competition'
      />
    ),
    []
  )

  const onClickAddGame = useCallback(({ onClick }) => {
    return <NewButton onClick={onClick} />
  }, [])

  const onClickNewPost = useCallback(({ onClick }) => {
    return <NewButton onClick={onClick} />
  }, [])

  const exportGameSlots = useCallback((load, onClick) => {
    return (
      <BaseActionButton
        // disabled={!gamesCreated || hasTournamentStarted}
        loading={load}
        onClick={onClick}
        name='download'
        title={'Export Game Slots'}
      />
    )
  }, [])

  const exportCompetition = useCallback((load, onClick) => {
    return (
      <BaseActionButton
        loading={load}
        onClick={onClick}
        name='download'
        title={t('common:exportComp')}
        id='export-competition'
      />
    )
  }, [])

  const onUploadGameSlots = useCallback(
    (label, onClick) => {
      return (
        <BaseActionButton
          disabled={!gamesCreated || hasTournamentStarted}
          name='upload'
          title={label}
          onClick={onClick}
        />
      )
    },
    [gamesCreated, hasTournamentStarted]
  )

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.schedule}
          entityId={SCHEDULE_ID}
        />,
        permission.update && (
          <EditButton
            key='schedule_edit_button'
            onClick={() => {
              setInitialState(item)
              setIsScheduleModalOpen(true)
            }}
            title={item?.name_full}
          />
        ),
      ]
    : []

  const baseActions = []

  const filterToggleButton = useMemo(() => {
    if (isFilterToggleDisabled) {
      return null
    }
    return (
      <BaseActionButton
        faType={areFiltersHidden ? 'far' : 'fas'}
        key='toggle-filter'
        name='filter'
        title={`Toggle Filters Visibility ${areFiltersHidden ? 'on' : 'off'}`}
        onClick={() => {
          setAreFiltersHidden((v) => !v)
        }}
      />
    )
  }, [isFilterToggleDisabled, areFiltersHidden])

  const scheduleShareUrl = `${appOrigin}${location.pathname}${
    location.search || ''
  }`

  const shareScheduleButton = useMemo(
    () => (
      <BaseActionButton
        title='Share Competition'
        id='share-schedule-button'
        key='share-competition'
        label='Share Competition'
        customIcon={<ShareIcon />}
        onClick={() => setIsShareModalOpen(true)}
        alwaysShowLabel={true}
      />
    ),
    []
  )

  const reprocessStatsButton = useMemo(() => {
    if (!permission.update) {
      return null
    }
    return (
      <ReprocessStats
        key='reprocess'
        id={SCHEDULE_ID}
        onSuccess={triggerRefresh}
      />
    )
  }, [permission.update, SCHEDULE_ID, triggerRefresh])

  const onPressTransactionReport = useCallback(async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?report_type=transaction&format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(
        text,
        `${generateRegistrationName(item, 'Transactions')}`
      )
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the transaction report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }, [SCHEDULE_ID, item])

  const onPressRevenueReport = useCallback(async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?report_type=revenue&format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(
        text,
        `${generateRegistrationName(item, 'Revenue')}`
      )
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the revenue report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }, [SCHEDULE_ID, item])

  const onPressTeamInfoReport = useCallback(async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?report_type=team_registrations&format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(
        text,
        `${generateRegistrationName(item, 'team_registrations')}`
      )
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the team info report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }, [SCHEDULE_ID, item])

  const onPressPlayerInfoReport = useCallback(async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?report_type=player_registrations&format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(
        text,
        `${generateRegistrationName(item, 'player_registrations')}`
      )
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the player info report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }, [SCHEDULE_ID, item])

  const onPressDelinquentReport = useCallback(async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?report_type=delinquent&format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(
        text,
        `${generateRegistrationName(item, 'past_due_payments')}`
      )
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the AR aging report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }, [SCHEDULE_ID, item])

  const items = useMemo(
    () => [
      {
        key: '1',
        label: (
          <a
            id='location-tutorial'
            target='_blank'
            rel='noopener noreferrer'
            css={css`
              cursor: pointer;
            `}
            onClick={async () => {
              setIsPrintLoading(true)
              try {
                const gameIds = await fetchAllGameIdsForPrint()
                if (gameIds?.length > 0) {
                  const isFirefox =
                    navigator.userAgent.indexOf('Firefox') !== -1

                  if (isFirefox) {
                    history.push({
                      pathname: ROUTES.SCORESHEETS_FIREFOX,
                      state: {
                        games: gameIds,
                        search: location.search,
                        isSoccer,
                      },
                    })
                  } else {
                    history.push({
                      pathname: ROUTES.SCORESHEETS,
                      state: {
                        games: gameIds,
                        search: location.search,
                        isSoccer,
                      },
                    })
                  }
                }
              } finally {
                setIsPrintLoading(false)
              }
            }}
          >
            {isPrintLoading ? <Spin size='small' /> : null} Print Scoresheets
          </a>
        ),
      },
      {
        key: '2',
        label: (
          <a
            id='location-tutorial'
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              printSignInSheetModalRef?.current?.openModal()
            }}
          >
            Print Sign-in Sheets
          </a>
        ),
      },
    ],
    [
      history,
      location.search,
      isSoccer,
      fetchAllGameIdsForPrint,
      isPrintLoading,
    ]
  )

  const handleOnClickFirstGame = useCallback(() => {}, [])

  const handleToggleRegistration = useCallback(() => {
    setInitialState({ ...item, highlightRegistration: true })
    setIsScheduleModalOpen(true)
  }, [item])

  const gameListComponent = useMemo(
    () => (
      <GameList
        sportId={item.sport_id}
        schedule_id={SCHEDULE_ID}
        slug={`/schedules/${SCHEDULE_ID}/games`}
        refreshKey={refreshKey}
        setVisibleGameIds={setVisibleGameIds}
        areFiltersHidden={areFiltersHidden}
        setFilterForExporting={setFilterForExporting}
        isSoccer={isSoccer}
        isHockey={isHockey}
        onClickFirstGame={handleOnClickFirstGame}
      />
    ),
    [
      item.sport_id,
      SCHEDULE_ID,
      refreshKey,
      setVisibleGameIds,
      areFiltersHidden,
      setFilterForExporting,
      isSoccer,
      isHockey,
      handleOnClickFirstGame,
    ]
  )

  const pages = useMemo(
    () => [
      TOURNAMENT_READY && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/bracket`,
        text: t('common:playoff_plural', { count: 2 }),
        component: (
          <ScheduleBracket
            id={SCHEDULE_ID}
            canUpdate={permission.update}
            setBracketScheduleId={setBracketScheduleId}
            setBracketDivision={setBracketDivision}
            setIsFullScreenBracketModalOpen={setIsFullScreenBracketModalOpen}
            setBracketCompetitionType={setBracketCompetitionType}
            isFullScreenBracketModalOpen={isFullScreenBracketModalOpen}
          />
        ),
        actions: [shareScheduleButton],
      },
      {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/schedule`,
        text: `${t('common:schedule')} (${item?.games_count || 0})`,
        tutorialId: 'sandbox-tabs-id-schedule',
        component: (
          <ScheduleSubTabs
            scheduleId={SCHEDULE_ID}
            isAdmin={permission.update}
            onSchedulePublished={() => {
              readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
            }}
            listContent={
              !loadingTeams && !hasTeams && item?.games_count < 1 ? (
                <EmptySchedule
                  isPlayoff={TOURNAMENT_READY}
                  canUpdate={permission.update}
                  scheduleId={SCHEDULE_ID}
                />
              ) : hasTeams && item?.games_count < 1 && permission.update ? (
                <ScheduleEmptyState
                  onGenerateSchedule={() => {
                    const params = new URLSearchParams(window.location.search)
                    params.set('view', 'admin')
                    history.replace({ search: params.toString() })
                  }}
                  onManualImport={() => {
                    document.getElementById('import-competition')?.click()
                  }}
                />
              ) : (
                gameListComponent
              )
            }
          />
        ),
        actions: [
          permission.update && (
            <FormSheet
              key='add-game'
              Button={onClickAddGame}
              form={gameForm(
                {
                  rootSchedule: item,
                  parentId: SCHEDULE_ID,
                  if_necessary_status: ifNecessaryStatus,
                  organization_id: item?.organization_id,
                  organization_name: item?.organization?.name_full,
                },
                true,
                item?.sport_id
              )}
              label={t('Web:createGame')}
              onSubmit={onAddGame}
              title={t('Web:createANewGame')}
            />
          ),

          <Dropdown
            menu={{ items }}
            placement='bottomLeft'
            trigger={['click']}
            key={'dropdown'}
            open={isPrintLoading ? true : undefined}
          >
            <div
              css={css`
                cursor: pointer;
                border: 1px solid ${colors.DEFAULT_FLAIR};
                display: flex;
                justify-content: center;
                align-items: center;
                padding-top: 4px;
                padding-bottom: 4px;
                padding-left: 8px;
                padding-right: 8px;
                margin-left: 8px;
                border-radius: 4px;
                &:hover {
                  border: 1px solid ${colors.NEUTRAL_0};
                }
              `}
            >
              <PrinterOutlined
                style={{
                  color: colors.WHITE,
                }}
              />
            </div>
          </Dropdown>,
          permission.update && (
            <BaseActionButton
              key='export-competition-btn'
              customIcon={<ExportIcon size={16} color={colors.WHITE} />}
              title='Export Competition'
              onClick={() => {
                document.getElementById('export-competition').click()
              }}
            />
          ),
          permission.update && (
            <BaseActionButton
              key='import-competition-btn'
              customIcon={<ImportIcon size={16} color={colors.WHITE} />}
              title='Import Schedule'
              onClick={() => {
                document.getElementById('import-competition').click()
              }}
            />
          ),
          permission.update && isProfessionalOrUltimateAccount && (
            <BaseActionButton
              key='plug'
              name='plug'
              title={'Generate JS Plugin Tag'}
              onClick={() => {
                generatePluginTagModalRef?.current?.openModal()
              }}
            />
          ),
          item?.games_count > 0 && filterToggleButton,
          shareScheduleButton,
        ],
      },
      {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/standings`,
        text: t('common:standings'),
        tutorialId: 'sandbox-tabs-id-standings',
        component: (
          <Standings
            scheduleReprocessingListener={scheduleReprocessingListener}
            setOutsideDismissed={setDismissed}
            dismissed={dismissed}
            id={SCHEDULE_ID}
            defaultSort={isSoccer ? 'PTS' : 'WPCTD'}
            areFiltersHidden={areFiltersHidden}
            setIsFilterToggleDisabled={setIsFilterToggleDisabled}
            refreshKey={refreshKey}
            isSoccer={isSoccer}
            isInFrame={isInFrame}
            isGenericSport={isGenericSport}
            sportId={item.sport_id}
            isAdmin={permission.admin}
          />
        ),
        actions: [
          permission.update && (
            <BaseActionButton
              key='plug'
              name='plug'
              title={'Generate JS Plugin Tag'}
              onClick={() => {
                generatePluginTagModalRef?.current?.openModal()
              }}
            />
          ),
          filterToggleButton,
          reprocessStatsButton,
          shareScheduleButton,
        ],
      },
      shouldDisplayStatistics && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/statistics`,
        text: t('common:statistics'),
        tutorialId: 'sandbox-tabs-id-statistics',
        component: (
          <Statistics
            scheduleReprocessingListener={scheduleReprocessingListener}
            setOutsideDismissed={setDismissed}
            dismissed={dismissed}
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            useTeamColumn
            areFiltersHidden={areFiltersHidden}
            setIsFilterToggleDisabled={setIsFilterToggleDisabled}
            refreshKey={refreshKey}
            isSoccer={isSoccer}
            isHockey={isHockey}
            isInFrame={isInFrame}
            isGenericSport={isGenericSport}
            sportId={item.sport_id}
          />
        ),
        actions: [
          permission.update && (
            <BaseActionButton
              key='plug'
              name='plug'
              title={'Generate JS Plugin Tag'}
              onClick={() => {
                generatePluginTagModalRef?.current?.openModal()
              }}
            />
          ),
          filterToggleButton,
          reprocessStatsButton,
          shareScheduleButton,
        ],
      },
      {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/suspensions`,
        text: t('common:suspension_plural', { count: 2 }),
        tutorialId: 'sandbox-tabs-id-suspensions',
        component: (
          <Suspensions
            key={refreshKey}
            exportId={SCHEDULE_ID}
            exportFileName={item?.name_full}
            loading={isSuspensionsLoading}
            suspensions={suspensions}
            refresh={refresh}
            scheduleId={SCHEDULE_ID}
            hasPermission={permission.admin}
            onlyDisplayActive={!permission.admin}
          />
        ),
        actions: [
          permission.admin && (
            <AddSuspensionSheet
              key='add-suspension'
              comp={item}
              onComplete={refresh}
            />
          ),
          shareScheduleButton,
        ],
      },

      {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/locations`,
        text: t('common:location_plural', { count: 2 }),
        component: (
          <Venues
            entityType={ENTITY_TYPES.schedule}
            id={SCHEDULE_ID}
            slug={'/venues'}
            query={{ schedule_id: SCHEDULE_ID }}
            refreshKey={refreshKey}
            triggerRefresh={triggerRefresh}
            parentPermission={permission}
            organizationId={item?.organization_id}
            organizationName={item?.organization?.name_full}
          />
        ),
        actions: [
          permission.update && (
            <NewButton
              key='add-venue'
              onClick={() => setIsLocationModalOpen(true)}
            />
          ),
          shareScheduleButton,
        ],
      },
      {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/feed`,
        text: t('common:feed'),
        tutorialId: 'sandbox-tabs-id-feed',
        component: (
          <EntityFeed
            entity={item}
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            refreshKey={refreshKey}
            canPost={permission.post || permission.update || permission.admin}
            canTargetAudience={permission.update || permission.admin}
          />
        ),
        actions: [
          permission.update && (
            <Mobile key='add-post'>
              <FormSheet
                label={t('Web:createANewPost')}
                title={t('Web:createANewPost')}
                form={feedItemForm({}, false)}
                onSubmit={async ({ imageData, ...form }) => {
                  const body = JSON.stringify(form)
                  const response = await req(
                    `/schedules/${SCHEDULE_ID}/social-feed`,
                    {
                      method: 'POST',
                      body,
                    }
                  )
                  if (imageData) {
                    await req(`/social-feed/${response.data.id}/image`, {
                      method: 'POST',
                      body: imageData,
                    })
                  }
                  triggerRefresh()
                }}
                Button={onClickNewPost}
              />
            </Mobile>
          ),
          shareScheduleButton,
        ],
      },
      permission.update && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/reports`,
        text: 'Reports',
        tutorialId: 'sandbox-tabs-id-reports',
        privatePage: true,
        component: (
          <NewReports
            entity={item}
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
          />
        ),
        actions: [shareScheduleButton],
      },
      !isInFrame &&
        permission.update && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/setup`,
          text: t('common:setup'),
          tutorialId: 'sandbox-tabs-id-setup',
          privatePage: true,
          component: (
            <ScheduleTree
              isSoccer={isSoccer}
              id={SCHEDULE_ID}
              orgId={item.organization.id}
              schedule={item}
              showSalesforceSettings={syncSalesforce}
              is_registration_on={item.is_registration_on}
              sportId={item.sport_id}
              genericSportId={item?.generic_sport_id}
              onToggleRegistration={handleToggleRegistration}
            />
          ),
          actions: [
            permission.update && (
              <CloneSchedule
                key='clone'
                schedule={item}
                scheduleId={SCHEDULE_ID}
              />
            ),
            shareScheduleButton,
          ],
        },
      !isInFrame &&
        TOURNAMENT_READY &&
        permission.update && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/gameslots`,
          text: t('common:gameSlot_plural', { count: 2 }),
          tutorialId: 'sandbox-tabs-id-gameslots',
          privatePage: true,
          component: (
            <GameSlots
              id={SCHEDULE_ID}
              canSyncGameSlots={canSyncGameSlots}
              bracketsCreated={item?.brackets_created}
              readItem={readItem}
              setSelectedGameSlotDivision={setSelectedGameSlotDivision}
              setSelectedGameSlotDivisionName={setSelectedGameSlotDivisionName}
              setGamesCreated={setGamesCreated}
              setHasTournamentStarted={setHasTournamentStarted}
              setOnFinishUploadGameslots={setOnFinishUploadGameslots}
              sportId={item.sport_id}
            />
          ),
          actions: [
            permission.update && selectedGameSlotDivision && (
              <FileDownload
                key='download-comp'
                button={exportGameSlots}
                url={`/schedules/${selectedGameSlotDivision}/gameslots/export/csv`}
                fileName={`${selectedGameSlotDivisionName}`}
                query={filterForExporting}
                label={`Download ${selectedGameSlotDivisionName} Game Slots`}
              />
            ),
            permission.update && selectedGameSlotDivision && (
              <ImportCSV
                key='upload-game-slots'
                button={onUploadGameSlots}
                entityType={ENTITY_TYPES.gameSlots}
                id={selectedGameSlotDivision}
                supportsXML
                onSuccess={() => {
                  triggerRefresh()
                  onFinishUploadGameslots()
                }}
              />
            ),
            <BaseActionButton
              key='question'
              name='question'
              title='Help'
              onClick={async () => {
                window.open(
                  'https://support.sportninja.com/league-competition-management-6',
                  '_blank'
                )
              }}
            />,
            shareScheduleButton,
          ],
        },
      displayEnabledRegistrationTab && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/registration`,
        tutorialId: 'sandbox-tabs-id-player-registration',
        text: 'Registration',
        privatePage: true,
        component: (
          <RegistrationPageEntry
            key='registration'
            scheduleId={SCHEDULE_ID}
            sportId={item.sport_id}
            setIsRegistrationFormOpen={setIsRegistrationFormOpen}
            setRegistration={setRegistration}
            setRegistrationTypes={setRegistrationTypes}
            setAvailableTaxes={setAvailableTaxes}
            setIsRegistrationSetup={setIsRegistrationSetup}
            refetchRegistrationFn={refetchRegistrationFn}
            refetchRegistrationInfoFn={refetchRegistrationInfoFn}
          />
        ),
        actions: [
          <div
            key='edit-registration'
            css={css`
              margin-right: 8px;
            `}
          >
            <HeaderActionButton
              text='Registration Setup'
              onClick={() => {
                setIsRegistrationFormOpen((o) => !o)
                setRegistration(registration)
              }}
              variant='primary'
              textWeight='regular'
              size='small'
              icon={
                <Icon name='cog' fontSize={12} color={colors.SECONDARY_300} />
              }
            />
          </div>,
          <Dropdown
            placement='bottom'
            key='download'
            menu={{
              items: [
                {
                  key: '1',
                  label: 'Revenue Report',
                  onClick: async () => {
                    await onPressRevenueReport()
                  },
                },
                {
                  key: '2',
                  label: 'Transaction Report',
                  onClick: async () => {
                    await onPressTransactionReport()
                  },
                },
                {
                  key: '4',
                  label: 'Team Registrations Report',
                  onClick: async () => {
                    await onPressTeamInfoReport()
                  },
                },
                {
                  key: '5',
                  label: 'Player Registrations Report',
                  onClick: async () => {
                    await onPressPlayerInfoReport()
                  },
                },
                {
                  key: '6',
                  label: 'Past Due Payments Report',
                  onClick: async () => {
                    await onPressDelinquentReport()
                  },
                },
              ],
            }}
          >
            <div
              css={css`
                border-radius: 4px;
                border: 1px solid ${colors.PRIMARY};
                padding: 4px;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
            >
              <Icon name='download' fontSize={13} />
            </div>
          </Dropdown>,
          shareScheduleButton,
        ],
      },
      displayDisabledRegistrationTab &&
        !isCanlan && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/registration`,
          text: 'Registration',
          privatePage: true,
          component: <div>Registration is disabled</div>,
          disabled: true,
          tooltipMessage: (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
                text-align: left;
              `}
            >
              <Text
                variant='paragraphSmall'
                color={colors.NEUTRAL_0}
                weight='regular'
              >
                Want to start accepting registrations? <br />
                <span
                  css={css`
                    color: ${colors.PRIMARY};
                    cursor: pointer;
                    &:hover {
                      text-decoration: underline;
                    }
                  `}
                  onClick={() => {
                    setIsScheduleModalOpen(true)
                    setInitialState({
                      ...item,
                      highlightRegistration: true,
                    })
                  }}
                >
                  You&apos;ll need to enable it in settings. Click here to do
                  that now.
                </span>
              </Text>
            </div>
          ),
        },
      permission.admin &&
        isRegistrationSetup && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/coupons`,
          text: 'Coupons',
          privatePage: true,
          component: (
            <CouponsPage
              scheduleId={SCHEDULE_ID}
              currencyCode={registration?.currency_code || 'CAD'}
              openCreateModalRef={openCouponCreateRef}
            />
          ),
          actions: [
            <NewButton
              key='add-coupon'
              onClick={() => openCouponCreateRef.current?.()}
            />,
            shareScheduleButton,
          ],
        },
      !isInFrame &&
        permission.update && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/officials`,
          text: t('common:official_plural', { count: 2 }),
          tutorialId: 'sandbox-tabs-id-officials',
          privatePage: true,
          component: (
            <OfficialListTable
              key='officials'
              entityType={ENTITY_TYPES.schedule}
              entityId={SCHEDULE_ID}
              slug={`/schedules/${SCHEDULE_ID}/officials`}
              refreshKey={refreshKey}
              isSoccer={isSoccer}
              isOrg={false}
              sportId={item.sport_id}
            />
          ),
          actions: [
            (isCanlan || features?.rolesPermissions) && (
              <AddOfficial
                key='add-official'
                entityType={ENTITY_TYPES.schedule}
                entityId={SCHEDULE_ID}
                permission={permission}
                onComplete={triggerRefresh}
                isSoccer={isSoccer}
                sportId={item.sport_id}
              />
            ),
            shareScheduleButton,
          ],
        },

      !isInFrame &&
        permission.admin && {
          to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/users`,
          text: t('common:user_plural', { count: 2 }),
          tutorialId: 'sandbox-tabs-id-users',
          privatePage: true,
          component: (
            <UserListTable
              key='users'
              entityType={ENTITY_TYPES.schedule}
              entityId={SCHEDULE_ID}
              slug={`/schedules/${SCHEDULE_ID}/permissions`}
              refreshKey={refreshKey}
            />
          ),
          actions: [
            (isCanlan || features?.rolesPermissions) && (
              <NewButton
                key='add-user'
                onClick={() => {
                  setEditingUser(null)
                  setIsUserModalOpen(true)
                }}
              />
            ),
            shareScheduleButton,
          ],
        },
    ],
    [
      TOURNAMENT_READY,
      SCHEDULE_ID,
      permission,
      isFullScreenBracketModalOpen,
      item,
      loadingTeams,
      hasTeams,
      gameListComponent,
      onClickAddGame,
      ifNecessaryStatus,
      onAddGame,
      items,
      isProfessionalOrUltimateAccount,
      isPrintLoading,
      filterToggleButton,
      features?.rolesPermissions,
      scheduleReprocessingListener,
      dismissed,
      isSoccer,
      areFiltersHidden,
      refreshKey,
      isInFrame,
      isGenericSport,
      reprocessStatsButton,
      shouldDisplayStatistics,
      isHockey,
      isSuspensionsLoading,
      suspensions,
      refresh,
      triggerRefresh,
      onClickNewPost,
      syncSalesforce,
      canSyncGameSlots,
      readItem,
      selectedGameSlotDivision,
      exportGameSlots,
      selectedGameSlotDivisionName,
      filterForExporting,
      onUploadGameSlots,
      displayEnabledRegistrationTab,
      isRegistrationSetup,
      displayDisabledRegistrationTab,
      onFinishUploadGameslots,
      onPressRevenueReport,
      onPressTransactionReport,
      onPressTeamInfoReport,
      onPressPlayerInfoReport,
      onPressDelinquentReport,
      registration,
      history,
      handleToggleRegistration,
      shareScheduleButton,
    ]
  )

  let filteredPages = pages

  if (frameConfig?.pages) {
    filteredPages = pages.filter((page) => {
      return frameConfig.pages.includes(page?.text)
    })
  }

  // Redirect to schedule root when the URL points at a tab the current user
  // can't see (e.g. permission gate filtered it out of `pages`). Without this,
  // the page chrome renders but the content area is blank. Skipped inside
  // iframe embeds — the host controls navigation there and action buttons
  // (including share) are suppressed anyway.
  useEffect(() => {
    if (isInFrame) {
      return
    }
    if (loading) {
      return
    }
    if (!item || Object.keys(item).length === 0) {
      return
    }
    if (!permission || Object.keys(permission).length === 0) {
      return
    }
    const currentPath = location.pathname
    if (currentPath === BASE_ROUTE) {
      return
    }
    const visibleTabs = pages.filter(Boolean)
    const matchesVisibleTab = visibleTabs.some((page) => {
      if (!page?.to) {
        return false
      }
      if (page.to === currentPath) {
        return true
      }
      return page.to.endsWith('/feed') && currentPath.startsWith(`${page.to}/`)
    })
    if (!matchesVisibleTab) {
      history.replace(BASE_ROUTE)
    }
  }, [
    isInFrame,
    loading,
    item,
    permission,
    pages,
    location.pathname,
    BASE_ROUTE,
    history,
  ])

  const handleCompetitionWizardSkip = useCallback(async () => {
    await updateCompetitionTutorialStatus('skipped')
  }, [updateCompetitionTutorialStatus])

  const handleCompetitionWizardComplete = useCallback(async () => {
    await updateCompetitionTutorialStatus('completed')
  }, [updateCompetitionTutorialStatus])

  return (
    <>
      <CompetitionSetupWizard
        isOpen={shouldShowCompetitionWizard}
        onClose={closeCompetitionWizard}
        onSkip={handleCompetitionWizardSkip}
        onComplete={handleCompetitionWizardComplete}
        onRefetch={() => {
          readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
        }}
        competitionId={SCHEDULE_ID}
        competitionName={item?.name_full || 'Competition'}
      />
      <Page
        competitionStartDate={item?.starts_at}
        competitionEndDate={item?.ends_at}
        contactInfo={{
          email: item?.email,
          website: item?.website,
          phone: item?.phone,
          support_email: item?.support_email,
          support_website: item?.support_website,
          support_phone: item?.support_phone,
        }}
        backgroundImageUrl={item?.background_image?.full_path}
        sport={sport}
        isSoccer={isSoccer}
        genericSport={genericSport}
        isTeamOrSchedule
        isSchedule={true}
        error={error}
        loading={loading}
        baseCrumb={
          error
            ? ROUTES.SCHEDULES.ROOT
            : `${ROUTES.ORG_ROOT}/${item?.organization?.id}/schedules`
        }
        baseRoute={BASE_ROUTE}
        baseTitle={item?.organization?.name_full}
        breadcrumbs={getBreadcrumbs(ROUTES.SCHEDULE_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={filteredPages}
        isPrivate={!IS_PUBLIC}
        title={item?.name_full}
        subTitle={
          item?.generic_sport_name ? `${item?.generic_sport_name}` : null
        }
        iconName={item.is_tournament ? 'trophy' : 'calendar-alt'}
        imageId={getImageThumbnailId(item) || item?.image?.full_path || null}
        titleActions={titleActions}
        preserveQueryFor={[
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/bracket`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/setup`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/gameslots`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/standings`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/statistics`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/schedule`,
        ]}
      />
      <ScheduleModal
        isFormOpen={isScheduleModalOpen}
        setIsFormOpen={setIsScheduleModalOpen}
        initialState={initialState || null}
        onCancel={() => {
          setInitialState(null)
          setIsScheduleModalOpen(false)
        }}
        triggerRefresh={() => {
          readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
          fetchScheduleSettings(SCHEDULE_ID)
        }}
        displayCanlanForm={syncSalesforce}
        addPending={addPending}
        isDaysmartConnectedAtOrgLevel={
          item?.organization?.integrations?.daysmart?.is_connected === true
        }
        sportId={item.sport_id}
      />
      <NewModal
        ref={generatePluginTagModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <GeneratePluginModal
          onPressClose={() => {
            generatePluginTagModalRef?.current?.closeModal()
          }}
          scheduleId={item?.id}
          isOrganization={false}
        />
      </NewModal>
      <NewModal
        ref={printSignInSheetModalRef}
        shouldCloseOnOverlayClick={false}
      >
        <PrintSignSheetsModal
          onPressClose={() => {
            printSignInSheetModalRef?.current?.closeModal()
          }}
          visibleGameIds={visibleGameIdsRef}
          fetchAllGameIds={fetchAllGameIdsForPrint}
          history={history}
        />
      </NewModal>
      <RegistrationSetup
        isRegistrationFormOpen={isRegistrationFormOpen}
        setIsRegistrationFormOpen={setIsRegistrationFormOpen}
        registration={registration}
        registrationTypes={registrationTypes}
        availableTaxes={availableTaxes}
        scheduleId={SCHEDULE_ID}
        accountId={item?.organization?.account_id}
        refetchRegistrationFn={refetchRegistrationFn}
        refetchRegistrationInfoFn={refetchRegistrationInfoFn}
      />
      <ReprocessingSettingsOverlay
        scheduleReprocessingListener={scheduleReprocessingListener}
        setOutsideDismissed={setDismissed}
      />
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        entityType='competition'
        entityName={item?.name_full || 'Competition'}
        shareUrl={scheduleShareUrl}
      />
      <FullScreenBracketModal
        isOpen={isFullScreenBracketModalOpen}
        onClose={() => setIsFullScreenBracketModalOpen(false)}
        scheduleId={bracketScheduleId}
        division={bracketDivision}
        competitionType={bracketCompetitionType}
      />
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        organizationId={item?.organization_id}
        onSuccess={() => {
          triggerRefresh()
          setIsLocationModalOpen(false)
        }}
      />
      {permission.update ? (
        <div
          css={css`
            opacity: 0;
            display: none;
          `}
        >
          <FileDownload
            key='download-comp'
            button={exportCompetition}
            url={`/schedules/${exportScheduleId}/export/csv`}
            fileName={item.name_full}
            query={filterForExporting}
          />
          <ImportCSV
            key='upload-comp'
            button={uploadCompetition}
            entityType={ENTITY_TYPES.schedule}
            id={SCHEDULE_ID}
            supportsXML
            onSuccess={handleRefreshScheduleAfterGamesChange}
          />
        </div>
      ) : null}
      <UserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        initialData={editingUser}
        entityType={ENTITY_TYPES.schedule}
        entityId={SCHEDULE_ID}
        onSuccess={() => {
          triggerRefresh()
          setIsUserModalOpen(false)
          setEditingUser(null)
        }}
      />
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
    genericSportProperties: getAllGenericSportProperties(state),
    sportTypes: getSports(state),
    me: getSelfUser(state),
  }
}

export default connect(mapStateToProps)(Schedule)
